<template>
  <div>
    <template v-if="readonly">
      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseAccommodationType') }}:</span>
        <span class="text--secondary">{{ accommodationTypeLabel }}</span>
      </v-list-item>

      <v-list-item v-if="value.accommodationType === 'rented'" dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseRentedFrom') }}:</span>
        <span class="text--secondary">{{ emptyValueFormatter(value.rentedFromName) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseEmployeeAccommodationFeeNet') }}:</span>
        <span class="text--secondary">{{ formatMoney(value.employeeAccommodationFeeNet, false) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseBedRentPriceGross') }}:</span>
        <span class="text--secondary">{{ formatMoney(value.bedRentPriceGross) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseFullRentPriceGross') }}:</span>
        <span class="text--secondary">{{ formatMoney(value.fullRentPriceGross) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseUtilitiesCostGross') }}:</span>
        <span class="text--secondary">{{ formatMoney(value.utilitiesCostGross) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseDepositAmountGross') }}:</span>
        <span class="text--secondary">{{ formatMoney(value.depositAmountGross) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseDepositPaid') }}:</span>
        <span class="text--secondary">{{ booleanLabel(value.depositPaid) }}</span>
      </v-list-item>

      <v-list-item v-if="value.depositPaid" dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseDepositPaidDate') }}:</span>
        <span class="text--secondary">{{ formatDateValue(value.depositPaidDate) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseNoticePeriodMonths') }}:</span>
        <span class="text--secondary">{{ formatMonthsValue(value.noticePeriodMonths) }}</span>
      </v-list-item>

      <v-list-item dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseCleanedByCompany') }}:</span>
        <span class="text--secondary">{{ booleanLabel(value.cleanedByCompany) }}</span>
      </v-list-item>

      <v-list-item v-if="value.cleanedByCompany" dense class="px-0 my-n2">
        <span class="font-weight-medium me-2">{{ $t('houseCleaningDeductionNet') }}:</span>
        <span class="text--secondary">{{ formatMoney(value.cleaningDeductionNet, false) }}</span>
      </v-list-item>
    </template>

    <template v-else>
      <div class="mb-3">
        <div class="text-caption text--secondary mb-1">{{ $t('houseAccommodationType') }}</div>
        <v-btn-toggle v-model="value.accommodationType" mandatory color="primary" class="d-flex">
          <v-btn value="company" class="flex-grow-1">{{ $t('houseAccommodationCompany') }}</v-btn>
          <v-btn value="rented" class="flex-grow-1">{{ $t('houseAccommodationRented') }}</v-btn>
        </v-btn-toggle>
      </div>

      <v-text-field
        v-if="value.accommodationType === 'rented'"
        v-model="value.rentedFromName"
        outlined
        dense
        hide-details="auto"
        :label="$t('houseRentedFrom')"
        :placeholder="$t('houseRentedFrom')"
        maxlength="150"
        class="mb-3"
      />

      <v-text-field
        v-model="value.employeeAccommodationFeeNet"
        type="number"
        min="0"
        step="0.01"
        outlined
        dense
        hide-details="auto"
        :label="$t('houseEmployeeAccommodationFeeNet')"
        :placeholder="$t('houseEmployeeAccommodationFeeNet')"
        class="mb-3"
      />

      <v-text-field
        v-model="value.bedRentPriceGross"
        type="number"
        min="0"
        step="0.01"
        outlined
        dense
        hide-details="auto"
        :label="$t('houseBedRentPriceGross')"
        :placeholder="$t('houseBedRentPriceGross')"
        class="mb-3"
      />

      <v-text-field
        v-model="value.fullRentPriceGross"
        type="number"
        min="0"
        step="0.01"
        outlined
        dense
        hide-details="auto"
        :label="$t('houseFullRentPriceGross')"
        :placeholder="$t('houseFullRentPriceGross')"
        class="mb-3"
      />

      <v-text-field
        v-model="value.utilitiesCostGross"
        type="number"
        min="0"
        step="0.01"
        outlined
        dense
        hide-details="auto"
        :label="$t('houseUtilitiesCostGross')"
        :placeholder="$t('houseUtilitiesCostGross')"
        class="mb-3"
      />

      <v-text-field
        v-model="value.depositAmountGross"
        type="number"
        min="0"
        step="0.01"
        outlined
        dense
        hide-details="auto"
        :label="$t('houseDepositAmountGross')"
        :placeholder="$t('houseDepositAmountGross')"
        class="mb-3"
      />

      <div class="mb-3">
        <div class="text-caption text--secondary mb-1">{{ $t('houseDepositPaid') }}</div>
        <v-btn-toggle v-model="value.depositPaid" mandatory color="primary">
          <v-btn :value="true">{{ $t('yes') }}</v-btn>
          <v-btn :value="false">{{ $t('no') }}</v-btn>
        </v-btn-toggle>
      </div>

      <v-menu
        v-if="value.depositPaid"
        ref="depositPaidDateMenu"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            v-model="value.depositPaidDate"
            :label="$t('houseDepositPaidDate')"
            readonly
            outlined
            dense
            clearable
            hide-details="auto"
            v-bind="attrs"
            class="mb-3"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="value.depositPaidDate"
          :first-day-of-week="1"
          :locale="$i18n.locale"
          @input="$refs.depositPaidDateMenu.save($event)"
        />
      </v-menu>

      <v-text-field
        v-model="value.noticePeriodMonths"
        type="number"
        min="0"
        step="1"
        outlined
        dense
        clearable
        hide-details="auto"
        :label="$t('houseNoticePeriodMonths')"
        :placeholder="$t('houseNoticePeriodMonths')"
        class="mb-3"
      />

      <div class="mb-3">
        <div class="text-caption text--secondary mb-1">{{ $t('houseCleanedByCompany') }}</div>
        <v-btn-toggle v-model="value.cleanedByCompany" mandatory color="primary">
          <v-btn :value="true">{{ $t('yes') }}</v-btn>
          <v-btn :value="false">{{ $t('no') }}</v-btn>
        </v-btn-toggle>
      </div>

      <v-text-field
        v-if="value.cleanedByCompany"
        v-model="value.cleaningDeductionNet"
        type="number"
        min="0"
        step="0.01"
        outlined
        dense
        hide-details="auto"
        :label="$t('houseCleaningDeductionNet')"
        :placeholder="$t('houseCleaningDeductionNet')"
        class="mb-3"
      />
    </template>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import { emptyValueFormatter, formatDateToMonthShort } from '@core/utils/filter'

export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { root }) {
    const accommodationTypeLabel = computed(() => {
      if (props.value.accommodationType === 'rented') {
        return root.$t('houseAccommodationRented')
      }

      return root.$t('houseAccommodationCompany')
    })

    const booleanLabel = (flag) => root.$t(flag ? 'yes' : 'no')

    const formatMoney = (amount, gross = true) => {
      if (amount === null || amount === undefined || amount === '') {
        return emptyValueFormatter()
      }

      const num = Number(amount)
      if (Number.isNaN(num)) {
        return emptyValueFormatter()
      }

      const suffix = gross ? ' PLN brutto' : ' PLN netto'
      return `${num.toFixed(2)}${suffix}`
    }

    const formatDateValue = (date) => {
      if (!date) {
        return emptyValueFormatter()
      }

      return formatDateToMonthShort(date, false)
    }

    const formatMonthsValue = (months) => {
      if (months === null || months === undefined || months === '') {
        return emptyValueFormatter()
      }

      const num = Number(months)
      if (Number.isNaN(num)) {
        return emptyValueFormatter()
      }

      return num
    }

    return {
      accommodationTypeLabel,
      booleanLabel,
      formatMoney,
      formatDateValue,
      formatMonthsValue,
      emptyValueFormatter,
    }
  },
}
</script>
